import { createContext, useCallback, useContext, useEffect, useState } from "react";
import axios from "axios";
import { API, useAuth } from "@/contexts/AuthContext";

const SettingsContext = createContext(null);

const client = axios.create({ baseURL: API, withCredentials: true });

export function SettingsProvider({ children }) {
  const { user } = useAuth() || {};
  const [settings, setSettings] = useState({ stages: [], services: [], sources: [] });
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const { data } = await client.get("/crm/settings");
      setSettings(data);
    } catch {
      /* auth may not be present yet — safe to ignore */
    } finally {
      setLoading(false);
    }
  }, []);

  // Refetch whenever the authenticated user changes (login/logout) so the
  // dropdowns get populated the moment the session is available.
  useEffect(() => {
    if (user) refresh();
    else setLoading(false);
  }, [user, refresh]);

  const update = async (key, values) => {
    const { data } = await client.put(`/crm/settings/${key}`, { values });
    setSettings((s) => ({ ...s, [key]: data }));
    return data;
  };

  return (
    <SettingsContext.Provider value={{ settings, loading, refresh, update }}>
      {children}
    </SettingsContext.Provider>
  );
}

export const useSettings = () => useContext(SettingsContext) || { settings: { stages: [], services: [], sources: [] }, loading: false, refresh: () => {}, update: async () => {} };

// Tailwind color -> stage badge class map. Any unknown color falls back to slate.
export const STAGE_COLOR_CLASSES = {
  slate: "bg-slate-100 text-slate-800 dark:bg-slate-800/60 dark:text-slate-200 border-slate-200 dark:border-slate-700",
  blue: "bg-blue-50 text-blue-800 dark:bg-blue-900/30 dark:text-blue-200 border-blue-200 dark:border-blue-800/50",
  indigo: "bg-indigo-50 text-indigo-800 dark:bg-indigo-900/30 dark:text-indigo-200 border-indigo-200 dark:border-indigo-800/50",
  amber: "bg-amber-50 text-amber-800 dark:bg-amber-900/30 dark:text-amber-200 border-amber-200 dark:border-amber-800/50",
  orange: "bg-orange-50 text-orange-800 dark:bg-orange-900/30 dark:text-orange-200 border-orange-200 dark:border-orange-800/50",
  emerald: "bg-emerald-50 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-200 border-emerald-200 dark:border-emerald-800/50",
  teal: "bg-teal-50 text-teal-800 dark:bg-teal-900/30 dark:text-teal-200 border-teal-200 dark:border-teal-800/50",
  purple: "bg-purple-50 text-purple-800 dark:bg-purple-900/30 dark:text-purple-200 border-purple-200 dark:border-purple-800/50",
  rose: "bg-rose-50 text-rose-800 dark:bg-rose-900/30 dark:text-rose-200 border-rose-200 dark:border-rose-800/50",
  pink: "bg-pink-50 text-pink-800 dark:bg-pink-900/30 dark:text-pink-200 border-pink-200 dark:border-pink-800/50",
  cyan: "bg-cyan-50 text-cyan-800 dark:bg-cyan-900/30 dark:text-cyan-200 border-cyan-200 dark:border-cyan-800/50",
  lime: "bg-lime-50 text-lime-800 dark:bg-lime-900/30 dark:text-lime-200 border-lime-200 dark:border-lime-800/50",
};

export const STAGE_COLOR_NAMES = Object.keys(STAGE_COLOR_CLASSES);

export const stageBadgeClass = (color) => STAGE_COLOR_CLASSES[color] || STAGE_COLOR_CLASSES.slate;
